import React, { useEffect, useRef, useCallback } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";

type Size = { width: number; height: number };

interface CanvasSizeProps {
    onResize: (canvasSize: Size) => void;
}

export const CanvasSize: React.FC<CanvasSizeProps> = ({
  onResize,
}) => {
    const { gl, camera, setSize, invalidate } = useThree();

    const last = useRef<Size>({ width: 0, height: 0 });
    const rafId = useRef<number | null>(null);

    const applyResize = useCallback(
      (widthRaw: number, heightRaw: number) => {
          const width = Math.round(widthRaw);
          const height = Math.round(heightRaw);

          if (width <= 0 || height <= 0) return;

          const prev = last.current;
          if (prev.width === width && prev.height === height) return;

          last.current = { width, height };

          const nextDpr = Math.min(window.devicePixelRatio || 1, 2);
          const curDpr = gl.getPixelRatio?.() ?? nextDpr;
          if (Math.abs(curDpr - nextDpr) > 0.001) gl.setPixelRatio(nextDpr);

          setSize(width, height, false);

          const cam = camera as THREE.PerspectiveCamera;
          if (cam.isPerspectiveCamera) {
              const nextAspect = width / height;
              if (Math.abs(cam.aspect - nextAspect) > 0.0001) {
                  cam.aspect = nextAspect;
                  cam.updateProjectionMatrix();
              }
          }

          invalidate();
          onResize({ width, height });
      },
      [camera, gl, invalidate, onResize, setSize]
    );

    const scheduleResizeFromElement = useCallback(
      (element: HTMLElement) => {
          const width = element.clientWidth;
          const height = element.clientHeight;

          if (rafId.current != null) cancelAnimationFrame(rafId.current);
          rafId.current = requestAnimationFrame(() => {
              rafId.current = null;
              applyResize(width, height);
          });
      },
      [applyResize]
    );

    useEffect(() => {
        const canvas = gl.domElement as HTMLCanvasElement;
        const target = (canvas.parentElement ?? canvas) as HTMLElement;

        scheduleResizeFromElement(target);

        const resizeObserver = new ResizeObserver(() => scheduleResizeFromElement(target));
        resizeObserver.observe(canvas);

        const onWindowResize = () => scheduleResizeFromElement(target);
        window.addEventListener("resize", onWindowResize, { passive: true });

        return () => {
            window.removeEventListener("resize", onWindowResize);
            resizeObserver.disconnect();
            if (rafId.current != null) cancelAnimationFrame(rafId.current);
        };
    }, [gl, scheduleResizeFromElement]);

    return null;
};
